import { Component, ChangeDetectionStrategy, Input, signal, effect, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule, CardModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslatePipe } from 'i18n';
import { Platform } from 'shared';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { Logger } from 'shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-referral-link',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    IconModule,
    TranslatePipe
  ],
  templateUrl: './referral-link.component.html',
  styleUrls: ['./referral-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReferralLinkComponent {
  @Input() referralUrl?: string;

  protected logger = new Logger('ReferralLinkComponent');
  private platform = inject(Platform);
  private inAppBrowser = inject(InAppBrowser);
  private destroyRef = inject(DestroyRef);

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  copied = signal(false);

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    // Auto-reset "copied" feedback after 2 seconds
    effect(() => {
      if (this.copied()) {
        const timeout = setTimeout(() => this.copied.set(false), 2000);
        this.destroyRef.onDestroy(() => clearTimeout(timeout));
      }
    });
  }

  // ────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────
  copyToClipboard(): void {
    if (!this.referralUrl) {
      this.logger.warn('No referral URL to copy');
      return;
    }

    // Modern Clipboard API (preferred)
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(this.referralUrl)
        .then(() => {
          this.logger.info('Referral URL copied to clipboard');
          this.copied.set(true);
        })
        .catch(err => {
          this.logger.error('Clipboard API failed:', err);
          this.fallbackCopy();
        });
    } else {
      // Fallback for older browsers
      this.fallbackCopy();
    }
  }

  private fallbackCopy(): void {
    const textarea = document.createElement('textarea');
    textarea.value = this.referralUrl!;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);

    try {
      textarea.select();
      const successful = document.execCommand('copy');
      if (successful) {
        this.logger.info('Referral URL copied using fallback');
        this.copied.set(true);
      } else {
        this.logger.error('Fallback copy failed');
      }
    } catch (err) {
      this.logger.error('Fallback copy error:', err);
    } finally {
      document.body.removeChild(textarea);
    }
  }

  openInNewWindow(): void {
    if (!this.referralUrl) {
      this.logger.warn('No referral URL to open');
      return;
    }

    const url = this.referralUrl.trim();

    // Basic URL validation
    if (!/^https?:\/\/[^\s$.?#].[^\s]*$/.test(url)) {
      this.logger.error('Invalid referral URL:', url);
      return;
    }

    if (this.platform.is('hybrid')) {
      // Ionic/Capacitor hybrid app → use system browser
      const browser = this.inAppBrowser.create(url, '_system');
      
      browser.on('exit')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
        browser.close();
      });
    } else {
      // Web → secure new tab
      window.open(url, '_blank', 'noopener,noreferrer');
      this.logger.info('Opened referral URL in new window:', url);
    }
  }
}